import { useEffect, useCallback, useRef } from 'react';
import {
  requestFullscreen,
  exitFullscreen,
  getFullscreenElement,
  addFullscreenChangeListener,
} from '../utils/fullscreenCompat';
import type { PlayerAction } from '../playerReducer';

const FULLSCREEN_CLASS = 'kollab-player-fullscreen-wrapper';

export function useFullscreen(
  fullscreenTarget: HTMLElement | null,
  dispatch: React.Dispatch<PlayerAction>,
  onFullscreenChange?: (isFullscreen: boolean) => void,
) {
  // Use a ref so toggleFullscreen is stable across renders
  const targetRef = useRef<HTMLElement | null>(null);
  targetRef.current = fullscreenTarget;

  const toggleFullscreen = useCallback(() => {
    if (!targetRef.current) return;

    if (getFullscreenElement()) {
      exitFullscreen();
    } else {
      requestFullscreen(targetRef.current);
    }
  }, []);

  const enterFullscreenMode = useCallback(() => {
    if (!targetRef.current || getFullscreenElement()) return;
    requestFullscreen(targetRef.current);
  }, []);

  const exitFullscreenMode = useCallback(() => {
    if (!getFullscreenElement()) return;
    exitFullscreen();
  }, []);

  useEffect(() => {
    const handleChange = () => {
      const isFs = !!getFullscreenElement();
      dispatch(isFs ? { type: 'ENTER_FULLSCREEN' } : { type: 'EXIT_FULLSCREEN' });
      onFullscreenChange?.(isFs);

      // Imperatively toggle the CSS class on the wrapper element
      // (it's outside the React tree, so we can't use JSX className)
      if (targetRef.current) {
        if (isFs) {
          targetRef.current.classList.add(FULLSCREEN_CLASS);
        } else {
          targetRef.current.classList.remove(FULLSCREEN_CLASS);
        }
      }
    };

    const cleanup = addFullscreenChangeListener(handleChange);
    return () => {
      cleanup();
      // Safety: remove class if tearing down while fullscreen
      targetRef.current?.classList.remove(FULLSCREEN_CLASS);
    };
  }, [dispatch, onFullscreenChange]);

  return { toggleFullscreen, enterFullscreen: enterFullscreenMode, exitFullscreen: exitFullscreenMode };
}
